import { Auth } from 'aws-amplify';
import {
  TAmplifySignUpUserData,
  TConfirmSignUpAmplifyProp,
  TSignInAmplifyDataToDb,
  TResendSignUpAmplifyProps,
  TForgotPasswordAmplifyWithEmailProp,
  TForgotPasswordAmplifyWithUsernameProp,
  TForgotPasswordAmplifyWithPhoneNumberProp,
  TForgotPasswordSubmitAmplifyDataUI,
  TAmplifySignInWithEmailUserData,
  TSignUpWithEmailAmplifyProp,
  TSignUpWithPhoneNumberAmplifyProp,
  TAmplifySignInWithPhoneNumberUserData,
  TCompleteNewPasswordUI,
  ICurrentAuthUser,
  TCompleteNewPassword,
} from 'data/types/amplify.types';
import {
  confirmSignUpAmplifyConvertor,
  forgotPasswordSubmitAmplifyConvertor,
} from 'data/convertors/amplify.convertors';
import {
  signUpWithEmailAmplifyConvertor,
  forgotPasswordWithEmailAmplifyConvertor,
} from 'data/convertors/emailAmplify.convertors';
import {
  signUpWithPhoneNumberAmplifyConvertor,
  forgotPasswordWithPhoneNumberAmplifyConvertor,
} from 'data/convertors/phoneNumberAmplify.convertors';

const attributes = {
  'custom:userType': 'tenant',
};

const clientMetadata = {
  version: 'v2',
};

export const currentAmplifyAuthUser = async (): Promise<ICurrentAuthUser> => {
  return Auth.currentAuthenticatedUser();
};

export const resendSignUpAmplifyService = async (
  props: TResendSignUpAmplifyProps,
): Promise<void> => {
  await Auth.resendSignUp(props.username, clientMetadata);
};

export const confirmSignUpAmplifyService = async (
  props: TConfirmSignUpAmplifyProp,
): Promise<void> => {
  const { username, verificationCode } =
    confirmSignUpAmplifyConvertor.toDb(props);

  return Auth.confirmSignUp(username, verificationCode.trim());
};

export const signOutAmplifyService = async (): Promise<null> => {
  return Auth.signOut();
};

export const forgotPasswordWithUsernameAmplifyService = async (
  props: TForgotPasswordAmplifyWithUsernameProp,
): Promise<null> => {
  const { username } = props;
  return Auth.forgotPassword(username, clientMetadata);
};

export const forgotPasswordSubmitAmplifyService = async (
  props: TForgotPasswordSubmitAmplifyDataUI,
): Promise<string> => {
  const { username, verificationCode, newPassword } =
    forgotPasswordSubmitAmplifyConvertor.toDb(props);
  return Auth.forgotPasswordSubmit(username, verificationCode, newPassword);
};

export const completeNewPasswordService = async (
  props: TCompleteNewPasswordUI,
): Promise<TCompleteNewPassword> => {
  const { user, newPassword } = props;
  return Auth.completeNewPassword(user, newPassword);
};

// EMAIL
export const signUpWithEmailAmplifyService = async (
  props: TSignUpWithEmailAmplifyProp,
): Promise<TAmplifySignUpUserData> => {
  const { email, password } = signUpWithEmailAmplifyConvertor.toDb(props);

  return Auth.signUp({
    username: email,
    password,
    attributes: { email, ...attributes },
    clientMetadata,
    autoSignIn: { enabled: true },
  });
};

export const signInWithEmailAmplifyService = async (
  props: TSignInAmplifyDataToDb,
): Promise<TAmplifySignInWithEmailUserData> => {
  const { username, password } = props;

  return Auth.signIn(username, password);
};

export const forgotPasswordWithEmailAmplifyService = async (
  props: TForgotPasswordAmplifyWithEmailProp,
): Promise<null> => {
  const { username } = forgotPasswordWithEmailAmplifyConvertor.toDb(props);
  return Auth.forgotPassword(username, clientMetadata);
};

// PHONE_NUMBER
export const signUpWithPhoneNumberAmplifyService = async (
  props: TSignUpWithPhoneNumberAmplifyProp,
): Promise<TAmplifySignUpUserData> => {
  const { phoneNumber, password } =
    signUpWithPhoneNumberAmplifyConvertor.toDb(props);

  return Auth.signUp({
    username: phoneNumber,
    password,
    attributes: { phone_number: phoneNumber, ...attributes },
    clientMetadata,
    autoSignIn: { enabled: true },
  });
};

export const signInWithPhoneNumberAmplifyService = async (
  props: TSignInAmplifyDataToDb,
): Promise<TAmplifySignInWithPhoneNumberUserData> => {
  const { username, password } = props;

  return Auth.signIn(username, password);
};

export const forgotPasswordWithPhoneNumberAmplifyService = async (
  props: TForgotPasswordAmplifyWithPhoneNumberProp,
): Promise<null> => {
  const { username } =
    forgotPasswordWithPhoneNumberAmplifyConvertor.toDb(props);
  return Auth.forgotPassword(username, clientMetadata);
};
